import { useState, useRef, useEffect } from 'react';
import { X, Edit2, Check, Paperclip } from 'lucide-react';
import { isValidImageFile, processImageFile } from '@/lib/imageUtils';
import type {
  Issue,
  Timestamp,
  ImageAttachment,
} from '@/types/reportApp';

interface IssueCardProps {
  issue: Issue;
  isEditing: boolean;
  readOnly?: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onUpdate: (issueId: string, updates: Partial<Issue>) => void;
  onApprove: () => void;
  onMoveToPending: () => void;
  onDismiss: () => void;
}

export function IssueCard({
  issue,
  isEditing,
  readOnly = false,
  onEdit,
  onCancelEdit,
  onUpdate,
  onApprove,
  onMoveToPending,
  onDismiss,
}: IssueCardProps) {
  const [editedFlag, setEditedFlag] = useState(issue.flag);
  const [editedTimestamps, setEditedTimestamps] = useState<Timestamp[]>(issue.timestamps);
  const [editedDescription, setEditedDescription] = useState(issue.description);
  const [editedDetails, setEditedDetails] = useState(issue.details || '');
  const [editedAttachments, setEditedAttachments] = useState<ImageAttachment[]>(
    issue.attachments || []
  );
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset edited state when entering/exiting edit mode or when issue changes
  useEffect(() => {
    setEditedFlag(issue.flag);
    setEditedTimestamps(issue.timestamps);
    setEditedDescription(issue.description);
    setEditedDetails(issue.details || '');
    setEditedAttachments(issue.attachments || []);
  }, [isEditing, issue]);

  // Close screenshot modal on ESC key and lock body scroll
  useEffect(() => {
    if (selectedScreenshot) {
      document.body.style.overflow = 'hidden';

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setSelectedScreenshot(null);
        }
      };
      window.addEventListener('keydown', handleKeyDown);

      return () => {
        document.body.style.overflow = 'unset';
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [selectedScreenshot]);

  const formatTimestamp = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 0) return '';
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2)}`;
    if (digits.length <= 6)
      return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4)}`;
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 6)}:${digits.slice(6, 8)}`;
  };

  const handleSave = () => {
    onUpdate(issue.id, {
      flag: editedFlag,
      timestamps: editedTimestamps,
      description: editedDescription,
      details: editedDetails || undefined,
      attachments: editedAttachments,
    });
  };

  const handleTimestampChange = (index: number, field: 'start' | 'end', value: string) => {
    const formatted = formatTimestamp(value);
    setEditedTimestamps((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: formatted };
      return updated;
    });
  };

  const addTimestamp = () => {
    setEditedTimestamps((prev) => [...prev, { start: '', end: '' }]);
  };

  const removeTimestamp = (index: number) => {
    setEditedTimestamps((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    setIsUploadingImage(true);
    const files = Array.from(fileList);
    const imageFiles = files.filter(isValidImageFile);

    if (imageFiles.length === 0) {
      alert('Please select valid JPEG or PNG images.');
      setIsUploadingImage(false);
      return;
    }

    try {
      const processedAttachments = await Promise.all(
        imageFiles.map(file => processImageFile(file))
      );

      const updatedAttachments = [...editedAttachments, ...processedAttachments];
      setEditedAttachments(updatedAttachments);

      if (!isEditing) {
        onUpdate(issue.id, { attachments: updatedAttachments });
      }
    } catch (error) {
      console.error('Failed to process images:', error);
      alert('Failed to process images. Please try again with smaller files.');
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    const updatedAttachments = editedAttachments.filter((att) => att.id !== attachmentId);
    setEditedAttachments(updatedAttachments);

    if (!isEditing) {
      onUpdate(issue.id, { attachments: updatedAttachments });
    }
  };

  const formatTimestamps = (timestamps: Timestamp[]) => {
    if (timestamps.length === 0) return null;
    return timestamps
      .map((ts) => {
        if (ts.start && ts.end) return `${ts.start} - ${ts.end}`;
        if (ts.start) return ts.start;
        return '';
      })
      .filter(Boolean)
      .join(', ');
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        {isEditing ? (
          <div className="flex gap-2 mt-1">
            <button
              onClick={() => setEditedFlag('red')}
              className={`w-6 h-6 rounded-full ${editedFlag === 'red' ? 'bg-[#EF4444]' : 'bg-[#EF4444]/30'} border-2 ${editedFlag === 'red' ? 'border-white' : 'border-transparent'}`}
            />
            <button
              onClick={() => setEditedFlag('orange')}
              className={`w-6 h-6 rounded-full ${editedFlag === 'orange' ? 'bg-[#F97316]' : 'bg-[#F97316]/30'} border-2 ${editedFlag === 'orange' ? 'border-white' : 'border-transparent'}`}
            />
          </div>
        ) : (
          <div
            className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${
              issue.flag === 'red' ? 'bg-[#EF4444]' : 'bg-[#F97316]'
            }`}
          />
        )}

        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {isEditing ? (
            <>
              <div className="mb-2">
                {editedTimestamps.map((ts, index) => (
                  <div key={index} className="flex gap-2 mb-2 items-center">
                    <input
                      type="text"
                      value={ts.start}
                      onChange={(e) => handleTimestampChange(index, 'start', e.target.value)}
                      placeholder="Start: 00:00:00:00"
                      className="flex-1 px-3 py-1.5 bg-white/10 border border-gray-300 rounded font-mono text-xs"
                    />
                    <input
                      type="text"
                      value={ts.end}
                      onChange={(e) => handleTimestampChange(index, 'end', e.target.value)}
                      placeholder="End: 00:00:00:00"
                      className="flex-1 px-3 py-1.5 bg-white/10 border border-gray-300 rounded font-mono text-xs"
                    />
                    {editedTimestamps.length > 1 && (
                      <button
                        onClick={() => removeTimestamp(index)}
                        className="p-1.5 hover:bg-[#EF4444]/20 text-[#EF4444] rounded transition-colors"
                        title="Remove timestamp"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addTimestamp}
                  className="text-sm px-4 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium hover:bg-blue-100 transition-colors"
                >
                  + Add Timestamp
                </button>
              </div>

              <textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                rows={2}
                placeholder="Issue title (e.g., Rule 1.2: Invalid codec format)"
                className="w-full mb-2 px-3 py-2 bg-white/10 border border-gray-300 rounded text-sm resize-none"
              />

              <textarea
                value={editedDetails}
                onChange={(e) => setEditedDetails(e.target.value)}
                rows={2}
                placeholder="Additional details (optional)"
                className="w-full mb-2 px-3 py-2 bg-white/10 border border-gray-300 rounded text-sm resize-none"
              />

              {!readOnly && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingImage}
                  className="mb-2 flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Paperclip className="w-4 h-4" />
                  Attach Files
                </button>
              )}

              {editedAttachments.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs text-gray-500 mb-2">
                    Attachments ({editedAttachments.length}):
                  </p>
                  <div className="grid grid-cols-[repeat(4,80px)] gap-2">
                    {editedAttachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="relative group cursor-pointer"
                        onClick={() => setSelectedScreenshot(attachment.base64)}
                      >
                        <div className="w-20 h-20 bg-white/10 backdrop-blur border border-gray-300 rounded-lg overflow-hidden hover:border-wppBlue transition-all">
                          <img
                            src={attachment.base64}
                            alt={attachment.filename}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {!readOnly && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveAttachment(attachment.id);
                            }}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-[#93DFE4] rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#B8EDF0] shadow-lg"
                            title="Remove attachment"
                          >
                            <X className="w-3 h-3 text-[#00142D]" />
                          </button>
                        )}
                        <p className="text-[10px] text-gray-500 mt-1 truncate w-20 text-center">
                          {attachment.filename}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!readOnly && isUploadingImage && (
                <p className="text-xs text-[#93DFE4] mb-2 animate-pulse">Uploading images...</p>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-sm">{issue.description}</span>
                {formatTimestamps(issue.timestamps) && (
                  <span className="font-mono text-xs text-blue-600">
                    {formatTimestamps(issue.timestamps)}
                  </span>
                )}
                {issue.fileNames && issue.fileNames.length > 1 && (
                  <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded">
                    Cross-file ({issue.fileNames.length} files)
                  </span>
                )}
              </div>
              {issue.details && (
                <p className="text-xs text-gray-600 mb-2 whitespace-pre-line">{issue.details}</p>
              )}
              {issue.fileNames && issue.fileNames.length > 0 && (
                <div className="flex flex-col gap-0.5 mt-2">
                  {issue.fileNames.map((fileName) => (
                    <p key={fileName} className="text-xs text-gray-400 font-mono">
                      {fileName}
                    </p>
                  ))}
                </div>
              )}

              {issue.attachments && issue.attachments.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">
                    Attachments ({issue.attachments.length}):
                  </p>
                  <div className="grid grid-cols-[repeat(4,80px)] gap-2">
                    {issue.attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="relative group cursor-pointer"
                        onClick={() => setSelectedScreenshot(attachment.base64)}
                      >
                        <div className="w-20 h-20 bg-white/10 backdrop-blur border border-gray-300 rounded-lg overflow-hidden hover:border-wppBlue transition-all">
                          <img
                            src={attachment.base64}
                            alt={attachment.filename}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {!readOnly && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveAttachment(attachment.id);
                            }}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-[#93DFE4] rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#B8EDF0] shadow-lg"
                            title="Remove attachment"
                          >
                            <X className="w-3 h-3 text-[#00142D]" />
                          </button>
                        )}
                        <p className="text-[10px] text-gray-500 mt-1 truncate w-20 text-center">
                          {attachment.filename}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {issue.screenshots && issue.screenshots.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">
                    AI Analysis Screenshots ({issue.screenshots.length}):
                  </p>
                  <div className="grid grid-cols-[repeat(4,80px)] gap-2">
                    {issue.screenshots.map((screenshot) => (
                      <div
                        key={screenshot.id}
                        className="relative group cursor-pointer"
                        onClick={() => setSelectedScreenshot(screenshot.url)}
                      >
                        <div className="w-20 h-20 bg-white/10 backdrop-blur border border-gray-300 rounded-lg overflow-hidden hover:border-wppBlue transition-all">
                          <img
                            src={screenshot.url}
                            alt="Analysis screenshot"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {screenshot.timestamp && (
                          <p className="text-[10px] text-gray-500 mt-1 text-center font-mono">
                            {screenshot.timestamp}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!readOnly && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingImage}
                  className="mt-2 flex items-center gap-2 px-3 py-1.5 text-xs bg-blue-50 text-blue-600 rounded-lg font-medium hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Paperclip className="w-3 h-3" />
                  Attach Files
                </button>
              )}

              {!readOnly && isUploadingImage && (
                <p className="text-xs text-[#93DFE4] mt-2 animate-pulse">Uploading images...</p>
              )}
            </>
          )}

          {/* Action Buttons */}
          {!readOnly && (
            <div className="flex gap-2 mt-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSave}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
                  >
                    <Check className="w-3 h-3" />
                    Save
                  </button>
                  <button
                    onClick={onCancelEdit}
                    className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={onEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors"
                  >
                    <Edit2 className="w-3 h-3" />
                    Edit
                  </button>
                  {issue.status === 'pending' && (
                    <>
                      <button
                        onClick={onApprove}
                        className="px-3 py-1.5 bg-[#10B981] text-white rounded-lg text-xs font-medium hover:bg-[#059669] transition-colors"
                      >
                        Approve
                      </button>
                      <button
                        onClick={onDismiss}
                        className="px-3 py-1.5 bg-gray-400 text-white rounded-lg text-xs font-medium hover:bg-gray-500 transition-colors"
                      >
                        Dismiss
                      </button>
                    </>
                  )}
                  {issue.status === 'approved' && (
                    <button
                      onClick={onMoveToPending}
                      className="px-3 py-1.5 bg-gray-400 text-white rounded-lg text-xs font-medium hover:bg-gray-500 transition-colors"
                    >
                      Move to Pending
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Screenshot Modal */}
      {selectedScreenshot && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedScreenshot(null)}
        >
          <div className="max-w-6xl max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedScreenshot(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 flex items-center gap-2"
            >
              <X className="w-6 h-6" />
              <span className="text-sm">Close (ESC)</span>
            </button>
            <img
              src={selectedScreenshot}
              alt="Screenshot full view"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
}
